import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_HREFS, ASSETS } from "../mock";
import { content } from "../i18n/content";
import { useLang, stripLangPrefix, withLang, LANG_PREF_KEY } from "../i18n/useLang";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useLang();
  const t = content[lang];
  const base = lang === "es" ? "/es" : "";
  const homePath = lang === "es" ? "/es/" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = useCallback(() => setOpen(false), []);

  const navItems = [
    { key: "solutions", label: t.nav.solutions, href: `${base}${NAV_HREFS.solutions}` },
    { key: "process", label: t.nav.process, href: `${base}${NAV_HREFS.process}` },
    { key: "partners", label: t.nav.partners, href: `${base}${NAV_HREFS.partners}` },
  ];

  const handleHashNavigation = useCallback(
    (e, href) => {
      closeMenu();
      const [path, hash] = href.split("#");

      if (location.pathname !== path || !hash) return;

      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [closeMenu, location.pathname]
  );

  const scrollToContact = useCallback(
    (e) => {
      e.preventDefault();
      closeMenu();

      if (location.pathname.replace(/\/$/, "") !== homePath.replace(/\/$/, "")) {
        navigate(`${base}/#contact`);
        return;
      }

      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [closeMenu, location.pathname, navigate, homePath, base]
  );

  const otherLang = lang === "es" ? "en" : "es";
  const otherLangPath = withLang(otherLang, stripLangPrefix(location.pathname)) + location.hash;

  const switchLang = useCallback(
    (nextLang) => () => {
      window.localStorage.setItem(LANG_PREF_KEY, nextLang);
      closeMenu();
    },
    [closeMenu]
  );

  const LangSwitch = ({ className = "" }) => (
    <div className={`flex items-center text-xs ${className}`}>
      <Link
        to={lang === "en" ? location.pathname + location.hash : otherLangPath}
        onClick={switchLang("en")}
        aria-current={lang === "en" ? "true" : undefined}
        className={lang === "en" ? "px-2 py-2.5 text-[color:var(--arroyo-navy)]" : "px-2 py-2.5 text-[color:var(--arroyo-muted)] hover:text-[color:var(--arroyo-navy)] transition-colors"}
      >
        EN
      </Link>
      <span className="text-[color:var(--arroyo-muted)]">|</span>
      <Link
        to={lang === "es" ? location.pathname + location.hash : otherLangPath}
        onClick={switchLang("es")}
        aria-current={lang === "es" ? "true" : undefined}
        className={lang === "es" ? "px-2 py-2.5 text-[color:var(--arroyo-navy)]" : "px-2 py-2.5 text-[color:var(--arroyo-muted)] hover:text-[color:var(--arroyo-navy)] transition-colors"}
      >
        ES
      </Link>
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 backdrop-blur-[20px] backdrop-saturate-[1.8] border-b ${
        scrolled ? "bg-[#fbfbfd]/80 border-black/10" : "bg-[#fbfbfd]/70 border-transparent"
      }`}
    >
      <div className="arroyo-container flex items-center justify-between h-16 md:h-[68px]">
        {/* Logo */}
        <Link to={homePath} className="shrink-0" onClick={closeMenu}>
          <span className="logo-badge">
            <img
              src={ASSETS.logo}
              alt="Arroyo Systems"
              className="logo-img h-8 md:h-10 w-auto object-contain"
            />
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8 xl:gap-11">
          {navItems.map((l) => (
            <Link key={l.key} to={l.href} className="nav-link" onClick={(e) => handleHashNavigation(e, l.href)}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LangSwitch />
          <a href={`${base}/#contact`} onClick={scrollToContact} className="contact-pill contact-pill--solid contact-pill--sm">
            {t.nav.contact}
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 -mr-2 text-[color:var(--arroyo-navy)]"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-[#fbfbfd]/95 backdrop-blur-[20px] ${
          open ? "max-h-[560px]" : "max-h-0"
        }`}
      >
        <div className="arroyo-container py-3 flex flex-col gap-1">
          {navItems.map((l) => (
            <Link
              key={l.key}
              to={l.href}
              className="nav-link nav-link--mobile"
              onClick={(e) => handleHashNavigation(e, l.href)}
            >
              {l.label}
            </Link>
          ))}
          <LangSwitch className="py-3 text-sm" />
          <a
            href={`${base}/#contact`}
            className="contact-pill contact-pill--solid self-start mt-2 mb-3"
            onClick={scrollToContact}
          >
            {t.nav.contact}
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;
